import { useState } from "react";
import {
  CheckCircle, Clock, Check, ChevronRight, Map,
  BookOpen, RotateCcw, Lock,
} from "lucide-react";

import LockedPlaceholder      from "../shared/LockedPlaceholder";
import LessonPlayer           from "../LessonPlayer";
import { TRAINING_SECTIONS, ALL_MODULE_IDS } from "../../data/trainingData";
import { LESSON_DATA_MAP }    from "../../data/lessonData";
import { ROADMAP_MILESTONES } from "../../data/goalsData";

// ─── Section label color map ──────────────────────────────────────────────────
const SECTION_COLORS = {
  products: { accent: "#0ea5e9", bg: "#f0f9ff", border: "#bae6fd" },
  sales:    { accent: "#ea580c", bg: "#fff7ed", border: "#fed7aa" },
  company:  { accent: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
};

export default function TrainingTab({ locked }) {
  // completedMods: Set of module ids the user has finished this session
  const [completedMods, setCompletedMods] = useState(new Set());
  // playerModule: the module currently open in LessonPlayer (with lessonData attached)
  const [playerModule,  setPlayerModule]  = useState(null);

  const totalModules    = ALL_MODULE_IDS.length;
  const completedCount  = ALL_MODULE_IDS.filter(id => completedMods.has(id)).length;
  const progressPct     = Math.round((completedCount / totalModules) * 100);

  function openPlayer(mod) {
  const lessonData = LESSON_DATA_MAP[mod.id];
  if (!lessonData) {
    console.error("Missing lesson data for:", mod.id);
    return;
  }
  setPlayerModule({ ...mod, lessonData });
}

  function handleComplete(modId) {
    setCompletedMods(s => new Set([...s, modId]));
    setPlayerModule(null);
  }

  function handleRetake(mod) {
    // Retake: remove from completed so status resets, then reopen
    setCompletedMods(s => { const n = new Set(s); n.delete(mod.id); return n; });
    openPlayer(mod);
  }

  if (locked) {
    return (
      <LockedPlaceholder
        title="Training Locked"
        desc="Complete your onboarding documents to unlock training materials."
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

      {/* ── Overall progress ── */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Training Progress</span>
          <span className="badge badge-blue">{completedCount}/{totalModules} complete</span>
        </div>
        <div className="card-body">
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 6 }}>
            <div style={{ flex: 1, background: "#e2e8f0", borderRadius: 6, height: 8, overflow: "hidden" }}>
              <div style={{ width: `${progressPct}%`, height: "100%", background: "#2563eb", borderRadius: 6, transition: "width 0.5s" }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#2563eb", flexShrink: 0 }}>{progressPct}%</span>
          </div>
          <p style={{ fontSize: 12, color: "#94a3b8" }}>
            {totalModules - completedCount} modules remaining · All modules can be retaken
          </p>
        </div>
      </div>

      {/* ── Sections ── */}
      {TRAINING_SECTIONS.map(section => {
        const sc = SECTION_COLORS[section.id] ?? SECTION_COLORS.company;
        return (
          <div key={section.id}>
            {/* Section header */}
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", margin: 0, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {section.label}
              </h3>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>{section.desc}</span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {section.modules.map(mod => {
                const isDone        = completedMods.has(mod.id);
                const isPlayerOpen  = playerModule?.id === mod.id;
                const isPlaceholder = !!mod.placeholder;

                return (
                  <div key={mod.id}>
                    {/* ── Module card ── */}
                    <div style={{
                      background: "#fff",
                      border: "1px solid #e2e8f0",
                      borderLeft: `3px solid ${isPlaceholder ? "#e2e8f0" : mod.color}`,
                      borderRadius: 10,
                      padding: "12px 16px",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      opacity: isPlaceholder ? 0.6 : 1,
                      borderBottomLeftRadius:  isPlayerOpen ? 0 : 10,
                      borderBottomRightRadius: isPlayerOpen ? 0 : 10,
                      borderBottom: isPlayerOpen ? "none" : undefined,
                    }}>
                      {/* Status icon */}
                      <div style={{
                        width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                        background: isDone ? "#f0fdf4" : isPlaceholder ? "#f1f5f9" : "#f8fafc",
                        border: `1.5px solid ${isDone ? "#86efac" : "#e2e8f0"}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {isDone        ? <CheckCircle size={16} color="#16a34a" />
                         : isPlaceholder ? <Clock size={15} color="#94a3b8" />
                         : <BookOpen size={15} color={mod.color} />}
                      </div>

                      {/* Title + subtitle */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 13.5, fontWeight: 600, color: "#1e293b" }}>{mod.title}</span>
                          {mod.required && (
                            <span className="badge badge-lost" style={{ fontSize: 10 }}>Required</span>
                          )}
                          {isDone && (
                            <span className="badge badge-green" style={{ fontSize: 10 }}>Completed</span>
                          )}
                          {isPlaceholder && (
                            <span style={{ fontSize: 10, color: "#94a3b8", background: "#f1f5f9", padding: "2px 8px", borderRadius: 20 }}>Coming Soon</span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{mod.subtitle}</div>
                      </div>

                      {/* Duration + actions */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>{mod.duration}</span>

                        {!isPlaceholder && (
                          isDone ? (
                            <button
                              className="btn btn-secondary btn-sm"
                              style={{ gap: 5 }}
                              onClick={() => handleRetake(mod)}
                            >
                              <RotateCcw size={12} /> Retake
                            </button>
                          ) : (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => openPlayer(mod)}
                            >
                              Start <ChevronRight size={13} />
                            </button>
                          )
                        )}
                      </div>
                    </div>

                    {/* ── LessonPlayer — renders inline below this module card ── */}
                    {isPlayerOpen && (
                      <div style={{ marginBottom: 4 }}>
                        <LessonPlayer
                          module={playerModule}
                          onClose={() => setPlayerModule(null)}
                          onComplete={() => handleComplete(mod.id)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* ── Ramp Roadmap ── */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">
            <Map size={15} style={{ display: "inline", marginRight: 6 }} />
            Ramp Roadmap
          </span>
        </div>
        <div className="card-body">
          <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>
            Your 90-day path from onboarding to fully ramped.
          </p>
          <div style={{ position: "relative", paddingLeft: 28 }}>
            <div style={{ position: "absolute", left: 10, top: 6, bottom: 6, width: 2, background: "#e2e8f0" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {ROADMAP_MILESTONES.map((m, i) => (
                <div key={i} style={{ position: "relative", display: "flex", gap: 14 }}>
                  <div style={{
                    position: "absolute", left: -28, top: 2,
                    width: 20, height: 20, borderRadius: "50%",
                    background: m.done ? "#2563eb" : "#fff",
                    border: `2px solid ${m.done ? "#2563eb" : "#cbd5e1"}`,
                    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1,
                  }}>
                    {m.done && <Check size={11} color="#fff" />}
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: m.done ? "#2563eb" : "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {m.week}
                      </span>
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: m.done ? "#0f172a" : "#475569" }}>
                        {m.label}
                      </span>
                      {m.done && <span className="badge badge-green" style={{ fontSize: 10 }}>Complete</span>}
                    </div>
                    <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}